// src/admin/AdminHeader.js

import React, { useState, useEffect } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext'; // To get user data and role
import { auth } from '../config/firebaseConfig'; // To use in logout
import { signOut } from 'firebase/auth'; // The actual logout function
import {
    ArrowLeftOnRectangleIcon,
    Bars3Icon,
    XMarkIcon,
    HomeIcon,
    CalendarDaysIcon,
    UserGroupIcon,
    EnvelopeIcon,
    UsersIcon,         // Icon for User Management
    ShoppingCartIcon   // Icon for Orders
} from '@heroicons/react/24/outline';

const AdminHeader = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { userData } = useAuth(); // Get the logged-in user's data from context
    const [isMenuOpen, setIsMenuOpen] = useState(false);

    // This effect closes the mobile menu when the page changes
    useEffect(() => {
        setIsMenuOpen(false);
    }, [location]);

    // UPDATED: Real logout functionality
    const handleLogout = async () => {
        try {
            await signOut(auth);
            // Navigate to login page after successful sign out
            navigate('/admin/login');
        } catch (error) {
            console.error("Failed to log out:", error);
        }
    };

    const toggleMenu = () => {
        setIsMenuOpen(!isMenuOpen);
    };

    // Get the user's first name from the displayName field
    const firstName = userData?.displayName?.split(' ')[0] || '';

    // Define all possible navigation links with the roles that can see them
    const allNavLinks = [
        { to: "/admin", icon: <HomeIcon />, text: "Dashboard", allowedRoles: ['admin', 'editor', 'marketing', 'order'] },
        { to: "/admin/manage-users", icon: <UsersIcon />, text: "Users", allowedRoles: ['admin'] },
        { to: "/admin/manage-events", icon: <CalendarDaysIcon />, text: "Events", allowedRoles: ['admin', 'editor'] },
        { to: "/admin/manage-professionals", icon: <UserGroupIcon />, text: "Professionals", allowedRoles: ['admin', 'marketing'] },
        { to: "/admin/manage-campaigns", icon: <EnvelopeIcon />, text: "Campaigns", allowedRoles: ['admin', 'marketing'] },
        { to: "/admin/manage-orders", icon: <ShoppingCartIcon />, text: "Orders", allowedRoles: ['admin', 'order'] }
    ];

    // Filter the links based on the current user's role
    const visibleNavLinks = allNavLinks.filter(link =>
        userData && link.allowedRoles.includes(userData.role)
    );

    return (
        <header className="admin-header">
            <div className="header-logo">
                LITTLE HIBBA <span>ADMIN</span>
            </div>

            {/* Navigation (This now dynamically shows links based on user role) */}
            <nav className={`admin-nav ${isMenuOpen ? 'active' : ''}`}>
                {visibleNavLinks.map((link) => (
                    <NavLink to={link.to} key={link.text} end={link.to === "/admin"}>
                        <span className="nav-icon">{link.icon}</span>
                        <span>{link.text}</span>
                    </NavLink>
                ))}
            </nav>

            {/* Right-side actions (Logout and Mobile Menu Button) */}
            <div className="header-actions">
                {/* NEW: Welcome message with the user's first name */}
                {firstName && (
                    <span className="welcome-message">
                        Welcome, {firstName}
                    </span>
                )}
                <button onClick={handleLogout} className="logout-button">
                    <ArrowLeftOnRectangleIcon />
                    <span>Logout</span>
                </button>
                <button className="menu-toggle" onClick={toggleMenu} aria-label="Toggle menu">
                    {isMenuOpen ? <XMarkIcon /> : <Bars3Icon />}
                </button>
            </div>
        </header>
    );
};

export default AdminHeader;